import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import { db } from "../../Firebase";
import "../../Admin.css";
import AdminNav from "../../components/AdminNav";

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function safeStr(v) {
  return String(v || "").trim();
}

function isoToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtDate(v) {
  if (!v) return "";
  if (v instanceof Date) return v.toLocaleDateString();
  if (typeof v?.toDate === "function") return v.toDate().toLocaleDateString();
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
    return v;
  }
  return "";
}

function money(n, currency = "USD") {
  const num = Number(n);
  if (!isFinite(num)) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

function statusClass(status) {
  const s = safeStr(status).toLowerCase();
  if (s === "paid") return "pill ok";
  if (s === "partial") return "pill info";
  if (s === "overdue") return "pill warn";
  if (s === "void") return "pill bad";
  return "pill";
}

function newGroup(id) {
  return {
    groupId: id,
    title: "",
    notes: "",
    items: [
      {
        itemId: `${id}-001`,
        category: "Work",
        name: "",
        description: "",
        qty: 1,
        unit: "ea",
        unitPrice: 0,
        taxable: true,
        photos: [],
        references: [],
        sortOrder: 1,
      },
    ],
  };
}

function newPayment() {
  return {
    at: isoToday(),
    method: "ACH",
    amount: 0,
    note: "",
  };
}

function linesToArray(text) {
  return String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function arrayToLines(arr) {
  return Array.isArray(arr) ? arr.join("\n") : "";
}

/**
 * Computes invoice totals from groups/pricing/payments.
 * Store totals on save to keep things stable and fast.
 */
function computeTotals(groups, pricing, payments) {
  const taxMode = pricing?.taxMode || "exclusive";
  const taxRate = safeNum(pricing?.taxRate);
  const discounts = Array.isArray(pricing?.discounts) ? pricing.discounts : [];
  const fees = Array.isArray(pricing?.fees) ? pricing.fees : [];

  let subtotal = 0;
  let taxableSubtotal = 0;

  for (const g of groups || []) {
    for (const it of g.items || []) {
      const line = safeNum(it.qty) * safeNum(it.unitPrice);
      subtotal += line;
      if (it.taxable) taxableSubtotal += line;
    }
  }

  let feesTotal = 0;
  let taxableFees = 0;
  for (const f of fees) {
    const amt = safeNum(f.amount);
    feesTotal += amt;
    if (f.taxable) taxableFees += amt;
  }

  let discountTotal = 0;
  for (const d of discounts) {
    const type = d.type || "percent";
    const value = safeNum(d.value);
    discountTotal += type === "percent" ? subtotal * value : value;
  }
  discountTotal = Math.max(0, Math.min(discountTotal, subtotal));

  const taxableRatio = subtotal > 0 ? taxableSubtotal / subtotal : 0;
  const taxableDiscountShare = discountTotal * taxableRatio;

  const taxableAfterDiscount = Math.max(0, taxableSubtotal - taxableDiscountShare);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountTotal);

  let taxTotal = 0;
  if (taxMode === "exclusive") taxTotal = (taxableAfterDiscount + taxableFees) * taxRate;

  const total = subtotalAfterDiscount + feesTotal + taxTotal;

  const paymentsTotal = (payments || []).reduce((acc, p) => acc + safeNum(p.amount), 0);
  const balanceDue = Math.max(0, total - paymentsTotal);

  return { subtotal, discountTotal, feesTotal, taxTotal, total, paymentsTotal, balanceDue };
}

function suggestStatus(currentStatus, totals) {
  const s = safeStr(currentStatus).toLowerCase();
  if (s === "void") return "void"; // preserve
  if (!totals) return currentStatus || "open";

  if (totals.balanceDue <= 0 && totals.total > 0) return "paid";
  if (totals.paymentsTotal > 0 && totals.balanceDue > 0) return "partial";
  if (totals.paymentsTotal <= 0 && totals.balanceDue > 0) return "open";
  return currentStatus || "open";
}

export default function AdminInvoiceEdit() {
  const nav = useNavigate();
  const { blobId } = useParams();

  const invoiceRef = useMemo(() => doc(db, "publicInvoices", blobId), [blobId]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [createdAtPersist, setCreatedAtPersist] = useState(null);

  const [form, setForm] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  useEffect(() => {
    document.title = "Edit Invoice | Admin";
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErrorMsg("");
      setStatusMsg("");

      try {
        const snap = await getDoc(invoiceRef);
        if (!snap.exists()) throw new Error(`Invoice not found: publicInvoices/${blobId}`);

        const data = snap.data() || {};
        if (!alive) return;

        setCreatedAtPersist(data.createdAt || null);

        // Normalize with sane defaults
        const normalized = {
          status: data.status || "open",
          invoiceNumber: data.invoiceNumber || `INV-${blobId}`,
          currency: data.currency || "USD",
          issueDate: data.issueDate || isoToday(),
          dueDate: data.dueDate || addDaysISO(14),

          issuer: data.issuer || {
            companyName: "Crowned Roofing",
            email: "",
            phone: "",
            website: "",
            address: { line1: "", line2: "", city: "", state: "TX", postal: "", country: "US" },
          },

          customer: data.customer || {
            type: "company",
            companyName: "",
            name: "",
            phone: "",
            email: "",
            billingAddress: { line1: "", line2: "", city: "", state: "TX", postal: "", country: "US" },
          },

          site: data.site || {
            propertyName: "",
            siteAddress: { line1: "", line2: "", city: "", state: "TX", postal: "", country: "US" },
          },

          source: data.source || { estimateId: "", selectedOptionId: "" },

          pricing: {
            taxMode: data.pricing?.taxMode || "exclusive",
            taxRate: safeNum(data.pricing?.taxRate) || 0,
            discounts:
              Array.isArray(data.pricing?.discounts) && data.pricing.discounts.length
                ? data.pricing.discounts
                : [{ label: "Discount", type: "percent", value: 0 }],
            fees:
              Array.isArray(data.pricing?.fees) && data.pricing.fees.length
                ? data.pricing.fees
                : [{ label: "Mobilization", amount: 0, taxable: false }],
          },

          groups:
            Array.isArray(data.groups) && data.groups.length
              ? data.groups.map((g) => ({
                  groupId: g.groupId || "G-01",
                  title: g.title || "",
                  notes: g.notes || "",
                  items:
                    Array.isArray(g.items) && g.items.length
                      ? g.items.map((it) => ({
                          itemId: it.itemId || `${g.groupId || "G-01"}-001`,
                          category: it.category || "Work",
                          name: it.name || "",
                          description: it.description || "",
                          qty: safeNum(it.qty),
                          unit: it.unit || "ea",
                          unitPrice: safeNum(it.unitPrice),
                          taxable: it.taxable !== false,
                          photos: Array.isArray(it.photos) ? it.photos : [],
                          references: Array.isArray(it.references) ? it.references : [],
                          sortOrder: safeNum(it.sortOrder) || 1,
                        }))
                      : [newGroup(g.groupId || "G-01").items[0]],
                }))
              : [newGroup("G-01")],

          payments: Array.isArray(data.payments) ? data.payments : [],

          notes: data.notes || "",
          payment: {
            methodsText: arrayToLines(data.payment?.paymentMethods || []),
            instructions: data.payment?.instructions || data.payment?.invoicing || "",
            lateFeePolicy: data.payment?.lateFeePolicy || "",
          },
          warranty: {
            type: data.warranty?.type || "",
            term: data.warranty?.term || "",
            detailsText: arrayToLines(data.warranty?.details || []),
          },
          attachments: Array.isArray(data.attachments) ? data.attachments : [],

          // keep but not required for editing
          totals: data.totals || null,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || null,
        };

        setForm(normalized);
        setEmailTo(data.customer?.email || "");
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load invoice.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [blobId, invoiceRef]);

  const computedTotals = useMemo(() => {
    if (!form) return null;
    return computeTotals(form.groups, form.pricing, form.payments);
  }, [form]);

  const effectiveStatus = useMemo(() => {
    if (!form) return "open";
    return suggestStatus(form.status, computedTotals);
  }, [form, computedTotals]);

  function setField(path, value) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }

  function updateGroup(groupIdx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.groups[groupIdx] = { ...next.groups[groupIdx], ...patch };
      return next;
    });
  }

  function updateItem(groupIdx, itemIdx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const it = next.groups[groupIdx].items[itemIdx];
      next.groups[groupIdx].items[itemIdx] = { ...it, ...patch };
      return next;
    });
  }

  function addGroup() {
    setForm((prev) => {
      const next = structuredClone(prev);
      const n = (next.groups?.length || 0) + 1;
      next.groups.push(newGroup(`G-${String(n).padStart(2, "0")}`));
      return next;
    });
  }

  function removeGroup(groupIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.groups.splice(groupIdx, 1);
      if (!next.groups.length) next.groups = [newGroup("G-01")];
      return next;
    });
  }

  function addItem(groupIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const g = next.groups[groupIdx];
      const n = (g.items?.length || 0) + 1;
      g.items.push({
        itemId: `${g.groupId}-${String(n).padStart(3, "0")}`,
        category: "Work",
        name: "",
        description: "",
        qty: 1,
        unit: "ea",
        unitPrice: 0,
        taxable: true,
        photos: [],
        references: [],
        sortOrder: 1,
      });
      return next;
    });
  }

  function removeItem(groupIdx, itemIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.groups[groupIdx].items.splice(itemIdx, 1);
      if (!next.groups[groupIdx].items.length) {
        next.groups[groupIdx].items.push(newGroup(next.groups[groupIdx].groupId).items[0]);
      }
      return next;
    });
  }

  function addPayment() {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.payments = Array.isArray(next.payments) ? next.payments : [];
      next.payments.push(newPayment());
      return next;
    });
  }

  function removePayment(idx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.payments.splice(idx, 1);
      return next;
    });
  }

  function updatePayment(idx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.payments[idx] = { ...next.payments[idx], ...patch };
      return next;
    });
  }

  function emailIsValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
  }

  function invoiceAddress(data) {
    const addr = data?.site?.siteAddress || data?.customer?.billingAddress || {};
    return [
      addr.line1,
      addr.line2,
      `${addr.city || ""}${addr.city && addr.state ? ", " : ""}${addr.state || ""} ${addr.postal || ""}`.trim(),
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(", ");
  }

  async function sendInvoiceEmail() {
    setEmailMsg("");

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_INVOICE_TEMPLATE_ID || "template_cqqm39k";
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setEmailMsg("Missing EmailJS config (.env).");
      return;
    }

    const to_email = String(emailTo || "").trim();
    if (!emailIsValid(to_email)) {
      setEmailMsg("Enter a valid recipient email.");
      return;
    }

    try {
      setEmailSending(true);

      const snap = await getDoc(invoiceRef);
      if (!snap.exists()) throw new Error("Invoice not found in Firestore.");

      const data = snap.data() || {};
      const totals = data.totals || computedTotals || {};
      const invoice_number = String(data.invoiceNumber || form?.invoiceNumber || blobId || "");
      const company_name = data.customer?.companyName || "";
      const contact_name = data.customer?.name || "";
      const client_name = contact_name || company_name || "Client";
      const invoice_date = fmtDate(data.issueDate || data.issuedAt || data.createdAt) || fmtDate(new Date());
      const due_date = fmtDate(data.dueDate);
      const invoice_url = `${window.location.origin}/invoice/${blobId}`;
      const balance_due = money(totals.balanceDue ?? 0, data.currency || form?.currency || "USD");
      const invoice_total = money(totals.total ?? 0, data.currency || form?.currency || "USD");
      const project_address = invoiceAddress(data);

      await emailjs.send(
        serviceId,
        templateId,
        {
          to_email,
          client_name,
          company_name,
          contact_name,
          invoice_number,
          invoice_date,
          due_date,
          project_address,
          invoice_total,
          balance_due,
          invoice_url,
        },
        { publicKey }
      );

      await setDoc(
        invoiceRef,
        {
          sentAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setEmailMsg(`Sent to ${to_email}`);
    } catch (e) {
      setEmailMsg(e?.message || e?.text || "Send failed.");
    } finally {
      setEmailSending(false);
    }
  }

  async function save(nextStatus) {
    if (!form) return;

    setSaving(true);
    setErrorMsg("");
    setStatusMsg("");

    try {
      const totals = computeTotals(form.groups, form.pricing, form.payments);
      const statusToWrite = nextStatus || suggestStatus(form.status, totals);

      const payload = {
        ...structuredClone(form),
        status: statusToWrite,
        totals: {
          subtotal: totals.subtotal,
          discountTotal: totals.discountTotal,
          feesTotal: totals.feesTotal,
          taxTotal: totals.taxTotal,
          total: totals.total,
          paymentsTotal: totals.paymentsTotal,
          balanceDue: totals.balanceDue,
        },
        updatedAt: serverTimestamp(),
      };

      payload.payment = {
        ...payload.payment,
        paymentMethods: linesToArray(payload.payment?.methodsText),
      };
      delete payload.payment.methodsText;

      payload.warranty = {
        ...payload.warranty,
        details: linesToArray(payload.warranty?.detailsText),
      };
      delete payload.warranty.detailsText;

      // preserve createdAt
      payload.createdAt = createdAtPersist || form.createdAt || serverTimestamp();

      // timestamps for states (optional)
      if (statusToWrite === "paid" && !payload.paidAt) payload.paidAt = serverTimestamp();
      if (statusToWrite === "void" && !payload.voidedAt) payload.voidedAt = serverTimestamp();

      await setDoc(invoiceRef, payload, { merge: true });

      setForm((prev) => ({ ...prev, status: statusToWrite, totals }));
      setStatusMsg("Saved.");
      window.setTimeout(() => setStatusMsg(""), 1400);
    } catch (e) {
      setErrorMsg(e?.message || "Failed to save invoice.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="aiPage">
      <AdminNav />
        <div className="aiError" style={{ background: "#f8fafc", color: "#334155", borderColor: "rgba(15,23,42,.10)" }}>
          Loading invoice…
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="aiPage">
      <AdminNav />
        <div className="aiError">{errorMsg || "Invoice not available."}</div>
        <button className="btn btnGhost" onClick={() => nav(-1)}>← Back</button>
      </div>
    );
  }

  return (
    <div className="aiPage">
      <AdminNav />
      <div className="aiTop">
        <div>
          <h1>Edit Invoice</h1>
          <p className="aiSub">
            Blob ID: <span className="aiMono">{blobId}</span>{" "}
            <span className={statusClass(effectiveStatus)} style={{ marginLeft: 10 }}>
              {effectiveStatus}
            </span>
          </p>
        </div>

        <div className="aiActions">
          <Link className="btn btnGhost" to="/admin/invoices">← Back</Link>
          <button className="btn btnGhost" disabled={saving} onClick={() => save("void")}>
            Void
          </button>
          <button className="btn" disabled={saving} onClick={() => save("paid")}>
            Mark Paid
          </button>
          <button className="btn btnPrimary" disabled={saving} onClick={() => save()}>
            Save
          </button>
          <a className="btn btnGhost" href={`/invoice/${blobId}`} target="_blank" rel="noreferrer">
            Public View
          </a>
        </div>
      </div>

      {statusMsg ? (
        <div className="aiError" style={{ background: "#ecfeff", color: "#155e75", borderColor: "rgba(6,182,212,.25)" }}>
          {statusMsg}
        </div>
      ) : null}
      {errorMsg ? <div className="aiError">{errorMsg}</div> : null}

      {/* EMAIL INVOICE */}
      <div className="aiTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Email Invoice</h3>
          <div className="aiMuted" style={{ fontSize: 12 }}>
            Sends the public invoice link via EmailJS.
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, marginTop: 10 }}>
          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>To Email</div>
            <input
              className="aiSearch"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="client@email.com"
              disabled={emailSending}
            />
          </label>

          <button
            className="btn btnPrimary"
            type="button"
            onClick={sendInvoiceEmail}
            disabled={emailSending || !emailIsValid(emailTo)}
            style={{ alignSelf: "end" }}
          >
            {emailSending ? "Sending..." : "Send"}
          </button>
        </div>

        <div className="aiMuted" style={{ fontSize: 12, marginTop: 10 }}>
          Link:{" "}
          <a href={`/invoice/${blobId}`} target="_blank" rel="noreferrer">
            {window.location.origin}/invoice/{blobId}
          </a>
        </div>

        {emailMsg ? (
          <div
            className="aiError"
            style={{
              marginTop: 10,
              background: String(emailMsg).startsWith("Sent") ? "#ecfeff" : "#fff1f2",
              color: String(emailMsg).startsWith("Sent") ? "#155e75" : "#9f1239",
              borderColor: String(emailMsg).startsWith("Sent")
                ? "rgba(6,182,212,.25)"
                : "rgba(244,63,94,.25)",
            }}
          >
            {emailMsg}
          </div>
        ) : null}
      </div>

      {/* IDENTITY */}
      <div className="aiTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Identity</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Status</div>
            <select className="aiSearch" value={form.status} onChange={(e) => setField("status", e.target.value)}>
              <option value="open">open</option>
              <option value="partial">partial</option>
              <option value="paid">paid</option>
              <option value="overdue">overdue</option>
              <option value="void">void</option>
            </select>
            <div className="aiMuted" style={{ fontSize: 11, marginTop: 6 }}>
              Suggested: <strong>{effectiveStatus}</strong>
            </div>
          </label>

          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Invoice #</div>
            <input className="aiSearch" value={form.invoiceNumber} onChange={(e) => setField("invoiceNumber", e.target.value)} />
          </label>

          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Issue Date</div>
            <input className="aiSearch" type="date" value={form.issueDate} onChange={(e) => setField("issueDate", e.target.value)} />
          </label>

          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Due Date</div>
            <input className="aiSearch" type="date" value={form.dueDate} onChange={(e) => setField("dueDate", e.target.value)} />
          </label>
        </div>
      </div>

      {/* CUSTOMER + SITE */}
      <div className="aiTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Customer & Site</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
            <strong>Customer</strong>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <input className="aiSearch" placeholder="Company Name" value={form.customer.companyName || ""} onChange={(e) => setField("customer.companyName", e.target.value)} />
              <input className="aiSearch" placeholder="Contact Name" value={form.customer.name || ""} onChange={(e) => setField("customer.name", e.target.value)} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input className="aiSearch" placeholder="Phone" value={form.customer.phone || ""} onChange={(e) => setField("customer.phone", e.target.value)} />
                <input className="aiSearch" placeholder="Email" value={form.customer.email || ""} onChange={(e) => setField("customer.email", e.target.value)} />
              </div>
              <input className="aiSearch" placeholder="Billing Address Line 1" value={form.customer.billingAddress?.line1 || ""} onChange={(e) => setField("customer.billingAddress.line1", e.target.value)} />
              <input className="aiSearch" placeholder="Billing Address Line 2" value={form.customer.billingAddress?.line2 || ""} onChange={(e) => setField("customer.billingAddress.line2", e.target.value)} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 120px", gap: 10 }}>
                <input className="aiSearch" placeholder="City" value={form.customer.billingAddress?.city || ""} onChange={(e) => setField("customer.billingAddress.city", e.target.value)} />
                <input className="aiSearch" placeholder="State" value={form.customer.billingAddress?.state || ""} onChange={(e) => setField("customer.billingAddress.state", e.target.value)} />
                <input className="aiSearch" placeholder="ZIP" value={form.customer.billingAddress?.postal || ""} onChange={(e) => setField("customer.billingAddress.postal", e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
            <strong>Site</strong>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <input className="aiSearch" placeholder="Property Name" value={form.site.propertyName || ""} onChange={(e) => setField("site.propertyName", e.target.value)} />
              <input className="aiSearch" placeholder="Site Address Line 1" value={form.site.siteAddress?.line1 || ""} onChange={(e) => setField("site.siteAddress.line1", e.target.value)} />
              <input className="aiSearch" placeholder="Site Address Line 2" value={form.site.siteAddress?.line2 || ""} onChange={(e) => setField("site.siteAddress.line2", e.target.value)} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 120px", gap: 10 }}>
                <input className="aiSearch" placeholder="City" value={form.site.siteAddress?.city || ""} onChange={(e) => setField("site.siteAddress.city", e.target.value)} />
                <input className="aiSearch" placeholder="State" value={form.site.siteAddress?.state || ""} onChange={(e) => setField("site.siteAddress.state", e.target.value)} />
                <input className="aiSearch" placeholder="ZIP" value={form.site.siteAddress?.postal || ""} onChange={(e) => setField("site.siteAddress.postal", e.target.value)} />
              </div>

              <div className="aiMuted" style={{ fontSize: 12, marginTop: 6 }}>
                Source: {form.source?.estimateId ? (
                  <span><strong>{form.source.estimateId}</strong>{form.source.selectedOptionId ? ` (Option ${form.source.selectedOptionId})` : ""}</span>
                ) : (
                  <span>—</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div className="aiTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Pricing</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Tax Mode</div>
            <select className="aiSearch" value={form.pricing.taxMode} onChange={(e) => setField("pricing.taxMode", e.target.value)}>
              <option value="exclusive">exclusive</option>
              <option value="inclusive">inclusive</option>
            </select>
          </label>

          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Tax Rate (0.0825 = 8.25%)</div>
            <input className="aiSearch" value={form.pricing.taxRate} onChange={(e) => setField("pricing.taxRate", e.target.value)} />
          </label>

          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Currency</div>
            <input className="aiSearch" value={form.currency} onChange={(e) => setField("currency", e.target.value)} />
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 12 }}>
          {/* Discounts */}
          <div style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>Discounts</strong>
              <button
                className="btn btnTiny btnGhost"
                type="button"
                onClick={() =>
                  setForm((prev) => {
                    const next = structuredClone(prev);
                    next.pricing.discounts.push({ label: "Discount", type: "percent", value: 0 });
                    return next;
                  })
                }
              >
                + Add
              </button>
            </div>

            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              {form.pricing.discounts.map((d, idx) => (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr auto", gap: 8 }}>
                  <input className="aiSearch" placeholder="Label" value={d.label || ""} onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.discounts[idx].label = v;
                      return next;
                    });
                  }} />
                  <select className="aiSearch" value={d.type || "percent"} onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.discounts[idx].type = v;
                      return next;
                    });
                  }}>
                    <option value="percent">percent</option>
                    <option value="flat">flat</option>
                  </select>
                  <input className="aiSearch" value={d.value} onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.discounts[idx].value = v;
                      return next;
                    });
                  }} />
                  <button className="btn btnTiny" type="button" onClick={() => {
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.discounts.splice(idx, 1);
                      if (!next.pricing.discounts.length) next.pricing.discounts.push({ label: "Discount", type: "percent", value: 0 });
                      return next;
                    });
                  }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Fees */}
          <div style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>Fees</strong>
              <button
                className="btn btnTiny btnGhost"
                type="button"
                onClick={() =>
                  setForm((prev) => {
                    const next = structuredClone(prev);
                    next.pricing.fees.push({ label: "Fee", amount: 0, taxable: false });
                    return next;
                  })
                }
              >
                + Add
              </button>
            </div>

            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              {form.pricing.fees.map((f, idx) => (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr auto", gap: 8 }}>
                  <input className="aiSearch" placeholder="Label" value={f.label || ""} onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.fees[idx].label = v;
                      return next;
                    });
                  }} />
                  <input className="aiSearch" value={f.amount} onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.fees[idx].amount = v;
                      return next;
                    });
                  }} />
                  <select className="aiSearch" value={f.taxable ? "yes" : "no"} onChange={(e) => {
                    const v = e.target.value === "yes";
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.fees[idx].taxable = v;
                      return next;
                    });
                  }}>
                    <option value="no">not taxable</option>
                    <option value="yes">taxable</option>
                  </select>
                  <button className="btn btnTiny" type="button" onClick={() => {
                    setForm((prev) => {
                      const next = structuredClone(prev);
                      next.pricing.fees.splice(idx, 1);
                      if (!next.pricing.fees.length) next.pricing.fees.push({ label: "Mobilization", amount: 0, taxable: false });
                      return next;
                    });
                  }}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live totals preview */}
        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>
            Subtotal: <strong>{money(computedTotals?.subtotal, form.currency)}</strong> •
            Discounts: <strong>{money(computedTotals?.discountTotal, form.currency)}</strong> •
            Fees: <strong>{money(computedTotals?.feesTotal, form.currency)}</strong> •
            Tax: <strong>{money(computedTotals?.taxTotal, form.currency)}</strong>
          </div>
          <div style={{ fontSize: 14 }}>
            Total: <strong>{money(computedTotals?.total, form.currency)}</strong> • Balance:{" "}
            <strong>{money(computedTotals?.balanceDue, form.currency)}</strong>
          </div>
        </div>
      </div>

      {/* SCOPE (GROUPS + ITEMS) */}
      <div className="aiTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Scope</h3>
          <button className="btn btnGhost" type="button" onClick={addGroup}>
            + Add group
          </button>
        </div>

        <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
          {form.groups.map((g, groupIdx) => (
            <div key={g.groupId} style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div className="aiMuted" style={{ fontSize: 12 }}>
                  <strong>{g.groupId}</strong>
                </div>
                <button className="btn btnTiny btnGhost" type="button" onClick={() => removeGroup(groupIdx)} disabled={form.groups.length <= 1}>
                  Remove group
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 12, marginTop: 8 }}>
                <label>
                  <div className="aiMuted" style={{ fontSize: 12 }}>Title</div>
                  <input className="aiSearch" value={g.title || ""} onChange={(e) => updateGroup(groupIdx, { title: e.target.value })} />
                </label>
                <label>
                  <div className="aiMuted" style={{ fontSize: 12 }}>Notes</div>
                  <input className="aiSearch" value={g.notes || ""} onChange={(e) => updateGroup(groupIdx, { notes: e.target.value })} />
                </label>
              </div>

              <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong>Line Items</strong>
                <button className="btn btnTiny btnGhost" type="button" onClick={() => addItem(groupIdx)}>
                  + Add item
                </button>
              </div>

              <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                {(g.items || []).map((it, itemIdx) => {
                  const lineTotal = safeNum(it.qty) * safeNum(it.unitPrice);
                  return (
                    <div key={it.itemId} style={{ background: "rgba(15,23,42,.02)", border: "1px solid rgba(15,23,42,.08)", borderRadius: 14, padding: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                        <div className="aiMuted" style={{ fontSize: 12 }}>
                          <strong>{it.itemId}</strong>
                        </div>
                        <button className="btn btnTiny" type="button" onClick={() => removeItem(groupIdx, itemIdx)} disabled={(g.items || []).length <= 1}>
                          Remove
                        </button>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                        <label>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Name</div>
                          <input className="aiSearch" value={it.name || ""} onChange={(e) => updateItem(groupIdx, itemIdx, { name: e.target.value })} />
                        </label>
                        <label>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Category</div>
                          <input className="aiSearch" value={it.category || ""} onChange={(e) => updateItem(groupIdx, itemIdx, { category: e.target.value })} />
                        </label>
                      </div>

                      <label style={{ display: "block", marginTop: 8 }}>
                        <div className="aiMuted" style={{ fontSize: 12 }}>Description</div>
                        <textarea className="aiSearch" style={{ minHeight: 70 }} value={it.description || ""} onChange={(e) => updateItem(groupIdx, itemIdx, { description: e.target.value })} />
                      </label>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
                        <label>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Qty</div>
                          <input className="aiSearch" value={it.qty} onChange={(e) => updateItem(groupIdx, itemIdx, { qty: e.target.value })} />
                        </label>
                        <label>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Unit</div>
                          <input className="aiSearch" value={it.unit || ""} onChange={(e) => updateItem(groupIdx, itemIdx, { unit: e.target.value })} />
                        </label>
                        <label>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Unit Price</div>
                          <input className="aiSearch" value={it.unitPrice} onChange={(e) => updateItem(groupIdx, itemIdx, { unitPrice: e.target.value })} />
                        </label>
                        <label>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Taxable</div>
                          <select className="aiSearch" value={it.taxable ? "yes" : "no"} onChange={(e) => updateItem(groupIdx, itemIdx, { taxable: e.target.value === "yes" })}>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                        <div>
                          <div className="aiMuted" style={{ fontSize: 12 }}>Line Total</div>
                          <div style={{ padding: "10px 12px" }}>
                            <strong>{money(lineTotal, form.currency)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* PAYMENTS */}
      <div className="aiTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <h3 style={{ margin: 0 }}>Payments</h3>
          <button className="btn btnGhost" type="button" onClick={addPayment}>
            + Add payment
          </button>
        </div>

        <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
          {(form.payments || []).length ? (
            form.payments.map((p, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "140px 140px 1fr 140px auto", gap: 10, alignItems: "center" }}>
                <input className="aiSearch" type="date" value={p.at || ""} onChange={(e) => updatePayment(idx, { at: e.target.value })} />
                <select className="aiSearch" value={p.method || "ACH"} onChange={(e) => updatePayment(idx, { method: e.target.value })}>
                  <option>ACH</option>
                  <option>Check</option>
                  <option>Cash</option>
                  <option>Card</option>
                  <option>Zelle</option>
                  <option>Wire</option>
                  <option>Other</option>
                </select>
                <input className="aiSearch" placeholder="Note" value={p.note || ""} onChange={(e) => updatePayment(idx, { note: e.target.value })} />
                <input className="aiSearch" placeholder="Amount" value={p.amount} onChange={(e) => updatePayment(idx, { amount: e.target.value })} />
                <button className="btn btnTiny" type="button" onClick={() => removePayment(idx)}>
                  Remove
                </button>
              </div>
            ))
          ) : (
            <div className="aiMuted">No payments recorded.</div>
          )}
        </div>

        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>
            Payments Total: <strong>{money(computedTotals?.paymentsTotal, form.currency)}</strong>
          </div>
          <div style={{ fontSize: 16 }}>
            Balance Due: <strong>{money(computedTotals?.balanceDue, form.currency)}</strong>
          </div>
        </div>
      </div>

      {/* RECEIPT DETAILS */}
      <div className="aiTableWrap" style={{ padding: 16 }}>
        <h3 style={{ margin: "0 0 8px" }}>Receipt Details</h3>

        <label style={{ display: "block", marginBottom: 10 }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>Receipt Notes</div>
          <textarea className="aiSearch" style={{ minHeight: 90 }} value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Warranty Type</div>
            <input className="aiSearch" value={form.warranty?.type || ""} onChange={(e) => setField("warranty.type", e.target.value)} />
          </label>
          <label>
            <div className="aiMuted" style={{ fontSize: 12 }}>Warranty Term</div>
            <input className="aiSearch" value={form.warranty?.term || ""} onChange={(e) => setField("warranty.term", e.target.value)} />
          </label>
        </div>

        <label style={{ display: "block", marginBottom: 10 }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>Warranty Details (one per line)</div>
          <textarea className="aiSearch" style={{ minHeight: 90 }} value={form.warranty?.detailsText || ""} onChange={(e) => setField("warranty.detailsText", e.target.value)} />
        </label>

        <label style={{ display: "block", marginBottom: 10 }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>Payment Methods (one per line)</div>
          <textarea className="aiSearch" style={{ minHeight: 70 }} value={form.payment?.methodsText || ""} onChange={(e) => setField("payment.methodsText", e.target.value)} />
        </label>

        <label style={{ display: "block", marginBottom: 10 }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>Payment Instructions</div>
          <textarea className="aiSearch" style={{ minHeight: 70 }} value={form.payment?.instructions || ""} onChange={(e) => setField("payment.instructions", e.target.value)} />
        </label>

        <label style={{ display: "block" }}>
          <div className="aiMuted" style={{ fontSize: 12 }}>Late Fee Policy</div>
          <textarea className="aiSearch" style={{ minHeight: 70 }} value={form.payment?.lateFeePolicy || ""} onChange={(e) => setField("payment.lateFeePolicy", e.target.value)} />
        </label>

        <div className="aiMuted" style={{ fontSize: 12, marginTop: 10 }}>
          Last updated: {fmtDate(form.updatedAt) || "—"}
        </div>
      </div>
    </div>
  );
}



